import type { ReactElement } from 'react';
import { PageShell } from '../Page';
import type { PageProps } from '../types';
import { EditableText } from '../../components/Editable';
import { ImageSlot } from '../../components/ImageSlot';
import { Frame, focalPointStyle } from './primitives';

/**
 * section_divider: a chapter break that belongs to no tactic — the title
 * slide in front of a `data_table`, or any section header a rep asks for.
 *
 * `product_divider` is the tactic-owned equivalent and stays exactly as it
 * is: it resolves a channel name from the catalog and falls back to media-kit
 * device art for its hero. Neither is right here. A divider titled "Radio
 * schedule" would render a laptop mockup, and the v3 contract reads
 * product_divider as "one clean chapter break per tactic"
 * (docs/momentum-v3-slides.md), so the agent needs a type that carries no
 * channel semantics at all.
 *
 * Layout classes are deliberately shared with the tactic divider
 * (`.mv3-divider-*`) so the two read as the same slide in a deck. The one
 * difference is the empty state: with no image this renders the neutral dark
 * field v3 global rule 6 asks for, never a placeholder card and never
 * borrowed device art.
 */
export function SectionDividerPage({ proposal, section, actions, readOnly }: PageProps): ReactElement {
  const title = section.title || 'Section';
  const imageUrl = section.content.imageUrl;

  return (
    <PageShell
      section={section}
      readOnly={readOnly}
      onTitleSave={(next) => actions.saveTitle(section, next)}
      className="mv3-divider-root"
      hideTitle
    >
      <Frame surface="dark" proposal={proposal} section={section}>
        <div className="mv3-divider-grid">
          <div className="mv3-divider-left">
            <h1 className="mv3-divider-name">{title}</h1>
            <div className="mv3-divider-descriptor">
              <EditableText
                value={section.content.headline}
                field="headline"
                placeholder="Add a one-line description (optional)"
                readOnly={readOnly}
                onSave={(headline) => actions.saveContent(section, { headline })}
              />
            </div>
          </div>
          <div className="mv3-divider-hero">
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={title}
                className="mv3-divider-image"
                style={focalPointStyle(section)}
              />
            ) : null}
            {!readOnly ? (
              <ImageSlot
                url={imageUrl}
                alt={title}
                readOnly={readOnly}
                onSave={(next) => actions.saveContent(section, { imageUrl: next })}
                className="mv3-divider-image-affordance"
                hideImage
                proposalId={proposal.proposalId}
                defaultQuery={title}
              />
            ) : null}
          </div>
        </div>
      </Frame>
    </PageShell>
  );
}
